/* eslint-disable no-empty */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'g33_program_token';

const ProgramContext = createContext(null);

const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};
const setStoredToken = (t) => {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
};

const apiFetch = async (path, opts = {}) => {
  const token = getStoredToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}${path}`, { ...opts, headers, credentials: 'include' });
  return res;
};

export const ProgramProvider = ({ children }) => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [modules, setModules] = useState([]);
  const [toolkit, setToolkit] = useState([]);
  const [images, setImages] = useState({}); // { code: { url, caption, ... } }

  // Probe existing session
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch('/api/gate33-program/check');
        if (r.ok) {
          const d = await r.json();
          if (d.authed) setAuthed(true);
        }
      } catch {}
      setChecking(false);
    })();
  }, []);

  const refreshImages = useCallback(async () => {
    try {
      const r = await apiFetch('/api/gate33-program/images');
      if (r.ok) {
        const d = await r.json();
        const map = {};
        for (const it of (d.items || [])) {
          if (it && it.code) map[it.code] = it;
        }
        setImages(map);
      }
    } catch {}
  }, []);

  const refresh = useCallback(async () => {
    try {
      const [rm, rt] = await Promise.all([
        apiFetch('/api/gate33-program/modules'),
        apiFetch('/api/gate33-program/toolkit'),
      ]);
      if (rm.ok) {
        const d = await rm.json();
        setModules(d.modules || []);
      }
      if (rt.ok) {
        const d = await rt.json();
        setToolkit(d.items || []);
      }
    } catch {}
    refreshImages();
  }, [refreshImages]);

  useEffect(() => {
    if (authed) refresh();
  }, [authed, refresh]);

  const login = useCallback(async (password) => {
    const r = await apiFetch('/api/gate33-program/auth', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    if (!r.ok) {
      if (r.status === 401) throw new Error('Wrong password.');
      throw new Error(`Server error (${r.status}).`);
    }
    const d = await r.json();
    if (d.access_token) setStoredToken(d.access_token);
    setAuthed(true);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await apiFetch('/api/gate33-program/logout', { method: 'POST' }); } catch {}
    setStoredToken('');
    setAuthed(false);
    setModules([]);
    setToolkit([]);
    setImages({});
  }, []);

  return (
    <ProgramContext.Provider
      value={{
        authed, checking,
        modules, toolkit,
        images, refreshImages,
        login, logout, refresh,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
};

export const useProgram = () => {
  const ctx = useContext(ProgramContext);
  if (!ctx) throw new Error('useProgram must be used inside <ProgramProvider>');
  return ctx;
};
